import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

@Injectable({
    providedIn: 'root',
})
export class TimeAndDateService {

    getTimezoneAbbreviation(timeZone: any): string {
        // If timeZone isn't a string, return a default or empty string.
        if (typeof timeZone !== 'string') {
            console.error('Invalid timezone provided:', timeZone);
            return 'Default'; // or return a default timezone abbreviation, e.g. 'CST'
        }

        try {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone,
                timeZoneName: 'short',
            });
            const parts = formatter.formatToParts(new Date());
            const tzNamePart = parts.find(part => part.type === 'timeZoneName');
            return tzNamePart ? tzNamePart.value : timeZone;
        } catch (error) {
            console.error("Error formatting timezone:", timeZone, error);
            return timeZone;
        }
    }

    formatTimeForTimezone(timeZone: string, timeStr: string | null): string {
        if (!timeStr) {
            return '';
        }
        // Extract the time portion, assuming format "HH:mm:ss-XX:XX"
        const [timePart] = timeStr.split('-');
        const dateStr = `2020-01-01T${timePart}`;
        const dt = dayjs.tz(dateStr, timeZone);
        return dt.format('h:mm A');
    }

    getIsoTimestamp(date: Date = new Date()): string {
        return date.toISOString().split('.')[0] + 'Z';
    }

    formatDateToMMDDYY(dateStr: string): string {
        const [year, month, day] = dateStr.split('-');
        const yy = year.slice(-2);
        return `${month}/${day}/${yy}`;
    }

    getFriendlyTimeAgo(date: Date): string {
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHr = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHr / 24);
        const diffMonth = Math.floor(diffDay / 30);

        if (diffSec < 60) return 'just now';
        if (diffMin < 60) return `${diffMin} min ago`;
        if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? 's' : ''} ago`;
        if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
        return `${diffMonth} month${diffMonth > 1 ? 's' : ''} ago`;
    }

    getDaysUntilDate(date: Date): string {
        const now = new Date().getTime();
        const then = date.getTime();
        let diffMs = then - now;

        if (diffMs <= 0) {
            return '0 days';  // already due or past
        }

        const msPerHour = 1000 * 60 * 60;
        const msPerDay = msPerHour * 24;

        // whole days
        const days = Math.floor(diffMs / msPerDay);
        if (days >= 1) {
            return `${days} day${days > 1 ? 's' : ''}`;
        }

        // less than 1 day → compute hours
        const hours = Math.floor(diffMs / msPerHour);
        return `${hours} hr${hours !== 1 ? 's' : ''}`;
    }

    firestoreTimestampToDate(ts: any): Date | null {
        if (ts && typeof ts.seconds === 'number') {
            return new Date(ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6));
        }
        return null;
    }

}
